import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
);

const colorPalette = [
  'rgba(75, 192, 192, 1)',
  'rgba(153, 102, 255, 1)',
  'rgba(255, 159, 64, 1)',
  'rgba(255, 99, 132, 1)',
  'rgba(54, 162, 235, 1)',
  'rgba(255, 206, 86, 1)',
  'rgba(75, 192, 192, 1)',
  'rgba(153, 102, 255, 1)',
  'rgba(255, 159, 64, 1)',
  'rgba(255, 99, 132, 1)',
];

const AXIS_EPSILON = 1e-9;

const getTickFormatter = (format) => {
  if (format === 'number') {
    return (value) => Number(value).toFixed(2);
  }

  return (value) => Number(value).toFixed(2) + '%';
};

const getAxisStats = (values = []) => {
  const minValue = values.length ? Math.min(0, ...values) : 0;
  const maxValue = values.length ? Math.max(0, ...values) : 0;

  return {
    minValue,
    maxValue,
    negativeExtent: Math.max(0, -minValue),
    positiveExtent: Math.max(0, maxValue),
  };
};

const getSharedZeroRatio = (statsEntries) => {
  const mixedEntries = statsEntries.filter(
    ({ negativeExtent, positiveExtent }) => negativeExtent > 0 && positiveExtent > 0,
  );

  if (mixedEntries.length) {
    const weightedRatioSum = mixedEntries.reduce((sum, { negativeExtent, positiveExtent }) => {
      const totalExtent = negativeExtent + positiveExtent;
      return sum + (negativeExtent / totalExtent) * totalExtent;
    }, 0);
    const totalWeight = mixedEntries.reduce(
      (sum, { negativeExtent, positiveExtent }) => sum + negativeExtent + positiveExtent,
      0,
    );

    return weightedRatioSum / totalWeight;
  }

  const hasPositiveOnly = statsEntries.some(
    ({ positiveExtent, negativeExtent }) => positiveExtent > 0 && negativeExtent === 0,
  );
  const hasNegativeOnly = statsEntries.some(
    ({ negativeExtent, positiveExtent }) => negativeExtent > 0 && positiveExtent === 0,
  );

  if (hasPositiveOnly && !hasNegativeOnly) {
    return 0;
  }
  if (hasNegativeOnly && !hasPositiveOnly) {
    return 1;
  }

  return 0.5;
};

const getAlignedAxisBounds = (axisStatsById, scalesConfig) => {
  const alignedAxisIds = Object.entries(scalesConfig)
    .filter(([axisId, scaleConfig]) => axisId !== 'x' && scaleConfig?.display !== false && scaleConfig?.alignZero)
    .map(([axisId]) => axisId);

  if (alignedAxisIds.length < 2) {
    return {};
  }

  const statsEntries = alignedAxisIds.map((axisId) => axisStatsById[axisId] || getAxisStats());
  const zeroRatio = getSharedZeroRatio(statsEntries);
  const safeLowerRatio = Math.max(zeroRatio, AXIS_EPSILON);
  const safeUpperRatio = Math.max(1 - zeroRatio, AXIS_EPSILON);

  return alignedAxisIds.reduce((accumulator, axisId) => {
    const { negativeExtent, positiveExtent } = axisStatsById[axisId] || getAxisStats();
    const span = Math.max(
      negativeExtent / safeLowerRatio,
      positiveExtent / safeUpperRatio,
      AXIS_EPSILON,
    ) * 1.05;

    accumulator[axisId] = {
      min: -zeroRatio * span,
      max: (1 - zeroRatio) * span,
    };

    return accumulator;
  }, {});
};

const LineChartComponent = ({ chartData }) => {
  const chartRef = React.useRef(null);
  const normalizedChartData = {
    title: chartData?.title || '',
    labels: Array.isArray(chartData?.labels) ? chartData.labels : [],
    datasets: Array.isArray(chartData?.datasets) ? chartData.datasets : [],
    scales:
      chartData?.scales && typeof chartData.scales === 'object'
        ? chartData.scales
        : {
            x: {
              type: 'time',
              time: {
                unit: 'month',
              },
            },
            y: {
              position: 'left',
              format: 'percent',
              valueMultiplier: 100,
            },
          },
  };

  const defaultYAxisId =
    Object.keys(normalizedChartData.scales).find((scaleKey) => scaleKey !== 'x') || 'y';

  const getScaleConfig = (axisId) => normalizedChartData.scales[axisId] || {};

  const scaleValue = (value, multiplier) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return null;
    }
    return numericValue * multiplier;
  };

  const datasetsWithDefaults = normalizedChartData.datasets.map((dataset, index) => {
    const axisId = dataset.yAxisID || defaultYAxisId;
    const scaleConfig = getScaleConfig(axisId);
    const multiplier = dataset.valueMultiplier ?? scaleConfig.valueMultiplier ?? 100;

    return {
      ...dataset,
      yAxisID: axisId,
      data: (Array.isArray(dataset.data) ? dataset.data : []).map((value) =>
        scaleValue(value, multiplier),
      ),
      borderColor: dataset.borderColor || colorPalette[index % colorPalette.length],
      backgroundColor: dataset.backgroundColor || dataset.borderColor || colorPalette[index % colorPalette.length],
      spanGaps: true,
    };
  });

  const axisValues = datasetsWithDefaults.reduce((accumulator, dataset) => {
    const axisId = dataset.yAxisID || defaultYAxisId;
    const values = (Array.isArray(dataset.data) ? dataset.data : []).filter((value) =>
      Number.isFinite(value),
    );
    accumulator[axisId] = [...(accumulator[axisId] || []), ...values];
    return accumulator;
  }, {});

  const axisStatsById = Object.fromEntries(
    Object.entries(axisValues).map(([axisId, values]) => [axisId, getAxisStats(values)]),
  );
  const alignedAxisBounds = getAlignedAxisBounds(axisStatsById, normalizedChartData.scales);

  const scales = Object.entries(normalizedChartData.scales).reduce(
    (accumulator, [scaleId, rawScaleConfig]) => {
      if (scaleId === 'x') {
        accumulator.x = {
          type: 'time',
          time: {
            unit: 'month',
          },
          title: {
            display: false,
          },
          ...rawScaleConfig,
        };
        return accumulator;
      }

      const {
        format = 'percent',
        ticks: rawTicks,
        grid: rawGrid,
        title: rawTitle,
        alignZero,
        ...scaleConfig
      } = rawScaleConfig || {};
      const axisStats = axisStatsById[scaleId] || getAxisStats();
      const alignedBounds = alignedAxisBounds[scaleId];

      accumulator[scaleId] = {
        type: 'linear',
        display: scaleConfig.display ?? true,
        position: scaleConfig.position || 'left',
        title: {
          display: false,
          ...(rawTitle || {}),
        },
        ticks: {
          callback: getTickFormatter(format),
          ...(rawTicks || {}),
        },
        min: scaleConfig.min ?? alignedBounds?.min,
        max: scaleConfig.max ?? alignedBounds?.max,
        suggestedMin: scaleConfig.suggestedMin ?? axisStats.minValue,
        suggestedMax: scaleConfig.suggestedMax ?? axisStats.maxValue,
        grid: {
          drawOnChartArea: (scaleConfig.position || 'left') !== 'right',
          color: (context) => {
            if (context.tick.value === 0) {
              return '#000000';
            }
            return 'rgba(0, 0, 0, 0.1)';
          },
          ...(rawGrid || {}),
        },
        alignZero,
        ...scaleConfig,
      };

      return accumulator;
    },
    {},
  );

  const options = {
    scales,
    plugins: {
      legend: {
        display: true,
      },
      title: {
        display: !!normalizedChartData.title,
        text: normalizedChartData.title,
        position: 'top',
        font: {
          size: 16,
        },
      },
    },
    elements: {
      line: {
        tension: 0.25,
      },
      point: { radius: 0 },
    },
  };

  return (
    <div style={{ position: 'relative' }}>
      <div ref={chartRef}>
        <Line data={{ ...normalizedChartData, datasets: datasetsWithDefaults }} options={options} />
      </div>
      <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center' }} />
    </div>
  );
};

export default LineChartComponent;
